import {isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import cn from 'classnames'
import type {SubNavBannerDocumentData} from 'prismicio-types'
import {useEffect, useRef, useState} from 'react'
import {useLocalStorage} from 'react-use'
import {Icon} from 'src/common/Icon'
import {matchesAnyPattern, parsePatterns} from 'src/common/utils/patternMatcher'

type SubNavBannerProps = {
  data: SubNavBannerDocumentData
  pageUid?: string
}

const themeMap: Record<string, {bg: string; text: string}> = {
  Blue: {bg: 'bg-moss-blue', text: 'text-moss-white'},
  'Green neon': {bg: 'bg-green-neon', text: 'text-moss-black'},
  'Green dark': {bg: 'bg-green-dark', text: 'text-moss-white'},
  'Moss black': {bg: 'bg-moss-black', text: 'text-moss-white'},
}

const widthMap: Record<string, string> = {
  Full: 'max-w-[1200px]',
  '80%': 'max-w-[960px]',
  '50%': 'max-w-[600px]',
}

const getPageUids = (pages: SubNavBannerDocumentData['included_pages']): string[] =>
  isFilled.group(pages)
    ? pages.reduce<string[]>((acc, item) => {
        if (isFilled.contentRelationship(item.page) && item.page.uid) {
          acc.push(item.page.uid)
        }

        return acc
      }, [])
    : []

export const SubNavBanner = ({data, pageUid}: SubNavBannerProps) => {
  const [isDismissed, setIsDismissed] = useLocalStorage(`sub-nav-banner-${data.banner_id}`, false)
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [bannerHeight, setBannerHeight] = useState(0)
  const bannerRef = useRef<HTMLDivElement>(null)

  // Track client-side mount to avoid SSR/hydration mismatch with localStorage
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Measure banner height and hide when scrolled past
  useEffect(() => {
    if (bannerRef.current) {
      setBannerHeight(bannerRef.current.offsetHeight)
    }

    const handleScroll = () => {
      if (!bannerRef.current) {
        return
      }

      const rect = bannerRef.current.getBoundingClientRect()
      setIsVisible(rect.top >= 0)
    }

    window.addEventListener('scroll', handleScroll, {passive: true})
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!data.text || !data.banner_id) {
    return null
  }

  const currentUid = pageUid || ''
  const showOnAll = data.show_on_all_pages === true

  const includedPages = getPageUids(data.included_pages)
  const excludedPages = getPageUids(data.excluded_pages)
  const includedPatterns = parsePatterns(data.included_patterns)
  const excludedPatterns = parsePatterns(data.excluded_patterns)

  const isIncluded = showOnAll || includedPages.includes(currentUid) || matchesAnyPattern(currentUid, includedPatterns)
  const isExcluded = excludedPages.includes(currentUid) || matchesAnyPattern(currentUid, excludedPatterns)

  if (!isIncluded || isExcluded) {
    return null
  }

  if (isMounted && isDismissed) {
    return null
  }

  const theme = themeMap[data.background_color ?? 'Green neon'] ?? themeMap['Green neon']
  const width = widthMap[data.width ?? 'Full'] ?? 'max-w-[1200px]'
  const hasLink = data.link_label && isFilled.link(data.link)

  return (
    <div
      ref={bannerRef}
      className={cn(
        'sticky top-[62px] z-40 w-full px-5 py-0.5 transition-opacity duration-200 xl:px-0',
        isVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      style={bannerHeight ? {marginBottom: -bannerHeight} : undefined}
    >
      <div className={cn('mx-auto rounded-[4px] py-3', theme.bg, theme.text, width)}>
        <div className="flex items-center justify-center gap-3 px-5">
          <div className="flex w-full flex-col items-center justify-center gap-2 md:flex-row md:gap-3">
            <span className="text-body-bold-s text-center">
              {data.mobile_text ? (
                <>
                  <span className="md:hidden">{data.mobile_text}</span>
                  <span className="hidden md:inline">{data.text}</span>
                </>
              ) : (
                data.text
              )}
            </span>
            {hasLink && (
              <PrismicNextLink
                field={data.link}
                className="group/textlink text-body-bold-s inline-flex items-center whitespace-nowrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="underline">{data.link_label}</span>
                <span
                  aria-hidden="true"
                  className="inline-block pl-1.5 no-underline duration-200 group-hover/textlink:translate-x-[3px]"
                >
                  →
                </span>
              </PrismicNextLink>
            )}
          </div>
          {data.is_dismissible && (
            <button
              aria-label="Dismiss banner"
              className="h-5 shrink-0 self-center"
              onClick={() => setIsDismissed(true)}
            >
              <Icon name="cross" className={cn('size-5', theme.text)} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
